import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button, Card, CardBody, Col, Container, Row } from 'reactstrap';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { 
      hasError: true,
      error,
      errorInfo: null
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
    this.setState({
      error,
      errorInfo
    });
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    window.location.href = '/';
  };

  private copyErrorDetails = () => {
    const { error, errorInfo } = this.state;
    
    // Collect all relevant information for debugging
    const errorDetails = {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      error: {
        message: error?.message || 'Unknown error',
        stack: error?.stack || 'No stack trace available'
      },
      componentStack: errorInfo?.componentStack || 'No component stack available',
      localStorage: {
        authUser: sessionStorage.getItem('authUser') ? 'present' : 'missing',
        layoutMode: localStorage.getItem('layout-mode') || 'not set'
      },
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      }
    };

    // Format for easy debugging
    const formattedText = `
=== MXR Space Error Report ===
Time: ${errorDetails.timestamp}
URL: ${errorDetails.url}

ERROR MESSAGE:
${errorDetails.error.message}

STACK TRACE:
${errorDetails.error.stack}

COMPONENT STACK:
${errorDetails.componentStack}

ENVIRONMENT:
- Browser: ${errorDetails.userAgent}
- Viewport: ${errorDetails.viewport.width}x${errorDetails.viewport.height}
- Auth: ${errorDetails.localStorage.authUser}
- Theme: ${errorDetails.localStorage.layoutMode}
    `.trim();

    // Copy to clipboard
    navigator.clipboard.writeText(formattedText).then(() => {
      // Show success toast or alert
      const toast = document.createElement('div');
      toast.className = 'alert alert-success position-fixed top-0 start-50 translate-middle-x mt-3';
      toast.style.zIndex = '9999';
      toast.innerHTML = '<i class="ri-check-line me-2"></i>Fehlerdetails kopiert!';
      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }).catch(err => {
      console.error('Failed to copy error details:', err);
      alert('Konnte Fehlerdetails nicht kopieren. Bitte öffnen Sie die Browser-Konsole.');
    });
  };

  public render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-danger">
                <CardBody>
                  <div className="text-center mb-4">
                    <h1 className="text-danger">
                      <i className="ri-error-warning-line" style={{ fontSize: '3rem' }}></i>
                    </h1>
                    <h3 className="text-danger">Ein Fehler ist aufgetreten</h3>
                  </div>
                  
                  <Alert color="danger" className="mb-3">
                    <strong>Fehler:</strong> {this.state.error?.message || 'Unbekannter Fehler'}
                  </Alert>

                  {isDevelopment && this.state.error && (
                    <>
                      <div className="mb-3">
                        <h5>Stack Trace:</h5>
                        <pre className="bg-light p-3 rounded" style={{ 
                          maxHeight: '300px', 
                          overflowY: 'auto',
                          fontSize: '0.85rem',
                          fontFamily: 'monospace'
                        }}>
                          {this.state.error.stack}
                        </pre>
                      </div>

                      {this.state.errorInfo && (
                        <div className="mb-3">
                          <h5>Component Stack:</h5>
                          <pre className="bg-light p-3 rounded" style={{ 
                            maxHeight: '200px', 
                            overflowY: 'auto',
                            fontSize: '0.85rem',
                            fontFamily: 'monospace'
                          }}>
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}

                      <Alert color="info" className="mb-3">
                        <strong>Tipp:</strong> Öffne die Browser-Konsole für weitere Details.
                      </Alert>
                    </>
                  )}

                  {!isDevelopment && (
                    <Alert color="warning" className="mb-3">
                      Bitte versuchen Sie die Seite neu zu laden oder kontaktieren Sie den Support, wenn das Problem weiterhin besteht.
                    </Alert>
                  )}

                  <div className="text-center">
                    <Button 
                      color="danger" 
                      onClick={this.copyErrorDetails}
                      className="me-2"
                    >
                      <i className="ri-clipboard-line me-1"></i>
                      Fehler kopieren
                    </Button>
                    <Button 
                      color="primary" 
                      onClick={this.handleReset}
                      className="me-2"
                    >
                      <i className="ri-home-line me-1"></i>
                      Zur Startseite
                    </Button>
                    <Button 
                      color="secondary" 
                      onClick={() => window.location.reload()}
                    >
                      <i className="ri-refresh-line me-1"></i>
                      Seite neu laden
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;